import React, { type ReactNode, useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, Slider, Typography, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { TIMELINE_SCALES, TIMELINE_SCALE_LABELS, TimelineScale } from '../utils/timelineUtils';

type TimelineScaleControlProps = {
	scale: TimelineScale;
	onScaleChange: (nextScale: TimelineScale) => void;
	zoom: number;
	minZoom: number;
	maxZoom: number;
	onZoomChange: (nextZoom: number) => void;
	zoomStep?: number;
	/** For short viewports (e.g. phone landscape): only the scale button and a single fully-in/fully-out zoom toggle. */
	isCompact?: boolean;
	/** Rendered at the top of the column, e.g. a view's own navigation button that has no room elsewhere. */
	topSlot?: ReactNode;
};

function TimelineScaleControl({
	scale,
	onScaleChange,
	zoom,
	minZoom,
	maxZoom,
	onZoomChange,
	zoomStep = 0.1,
	isCompact = false,
	topSlot,
}: TimelineScaleControlProps) {
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const [scaleMenuAnchor, setScaleMenuAnchor] = useState<HTMLElement | null>(null);
	const isZoomReset = zoom === 1;
	const toggleZoom = () => onZoomChange(isZoomReset ? minZoom : 1);

	return (
		<Box
			sx={{
				position: 'absolute',
				left: isCompact ? 4 : 16,
				bottom: isCompact ? 8 : 32,
				zIndex: (theme) => theme.zIndex.fab,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: isMobile ? 1 : 1.5,
				pointerEvents: 'auto',
				userSelect: 'none',
			}}
			onClick={(e) => e.stopPropagation()}
			onPointerDown={(e) => e.stopPropagation()}
		>
			{topSlot}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: isMobile ? 1 : 1.5,
					...(!isMobile && {
						opacity: 0.5,
						'&:hover': { opacity: 1 },
					}),
				}}
			>
				<Button
					variant='text'
					size='small'
					aria-label='Change timeline scale'
					onClick={(e) => setScaleMenuAnchor(e.currentTarget)}
					tabIndex={-1}
					sx={{ minWidth: 64, color: 'text.secondary', textTransform: 'none' }}
				>
					{TIMELINE_SCALE_LABELS[scale]}
				</Button>
				<Menu
					anchorEl={scaleMenuAnchor}
					open={Boolean(scaleMenuAnchor)}
					onClose={() => setScaleMenuAnchor(null)}
					anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
					transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				>
					{TIMELINE_SCALES.map((option) => (
						<MenuItem
							key={option}
							selected={option === scale}
							onClick={() => {
								onScaleChange(option);
								setScaleMenuAnchor(null);
							}}
						>
							{TIMELINE_SCALE_LABELS[option]}
						</MenuItem>
					))}
				</Menu>
				{isCompact && (
					<IconButton size='small' aria-label={isZoomReset ? 'Zoom timeline all the way out' : 'Reset timeline zoom'} onClick={toggleZoom} tabIndex={-1}>
						{isZoomReset ? <ZoomOutIcon /> : <ZoomInIcon />}
					</IconButton>
				)}
				{!isCompact && isMobile && (
					<>
						<IconButton
							size='small'
							aria-label='Zoom in timeline'
							onClick={() => onZoomChange(zoom + zoomStep)}
							disabled={zoom >= maxZoom}
							sx={{ color: 'text.secondary' }}
							tabIndex={-1}
						>
							<ZoomInIcon fontSize='small' />
						</IconButton>
						<Button variant='text' size='small' onClick={toggleZoom} tabIndex={-1}>
							<Typography variant='body2' color='text.secondary' sx={{ minWidth: 44, textAlign: 'center' }}>
								{`${Math.round(zoom * 100)}%`}
							</Typography>
						</Button>
						<IconButton
							size='small'
							aria-label='Zoom out timeline'
							onClick={() => onZoomChange(zoom - zoomStep)}
							disabled={zoom <= minZoom}
							sx={{ color: 'text.secondary' }}
							tabIndex={-1}
						>
							<ZoomOutIcon fontSize='small' />
						</IconButton>
					</>
				)}
				{!isCompact && !isMobile && (
					<>
						<IconButton size='small' aria-label={isZoomReset ? 'Zoom timeline all the way out' : 'Reset timeline zoom'} onClick={toggleZoom} tabIndex={-1}>
							{isZoomReset ? <ZoomOutIcon /> : <ZoomInIcon />}
						</IconButton>
						<Slider
							orientation='vertical'
							min={Math.log2(minZoom)}
							max={Math.log2(maxZoom)}
							step={0.01}
							value={Math.log2(zoom)}
							onChange={(_event, value) => onZoomChange(2 ** (Array.isArray(value) ? value[0] : value))}
							aria-label='Timeline zoom'
							tabIndex={-1}
							sx={{
								height: 100,
								'& .MuiSlider-thumb': { width: 14, height: 14 },
							}}
						/>
						<Typography variant='caption' color='text.secondary' sx={{ minWidth: 36, textAlign: 'center' }}>
							{`${Math.round(zoom * 100)}%`}
						</Typography>
					</>
				)}
			</Box>
		</Box>
	);
}

export default TimelineScaleControl;
